import { type JWTPayload } from 'jose';
import { defineStore } from 'pinia';

import { api } from 'src/boot/axios';

interface State {
  /** Namespace da aplicação (dev ou prd) */
  env: 'local' | 'dev' | 'prd';
  loggedUser?: LoggedUser | null;
  resetToken: string;
}

export type Role = 'admin' | 'manager' | 'viewer';

export interface LoggedUser {
  email: string;
  global: boolean;
  origin: string;
  role: Role;
  name: string;
}

export interface AccessTokenJWT extends JWTPayload {
  /**
   * Customer sendo acessado. Pode não existir em caso de usuário global
   * que ainda não acessou um cliente específico.
   */
  aud?: string;
  /** Origem do login */
  sub: 'login' | 'dashboard' | 'crm' | 'terminal';
  /** Primeiro nome do usuário */
  name: string;
  /** Email do usuário */
  email: string;
  /** Nível de permissão do usuário */
  role: Role;
  /** Se o usuário é do tipo global */
  global?: boolean;
}

export interface ResetTokenJWT extends JWTPayload {
  /** Email do usuário */
  email: string;
}

export interface AccessTokenResponse {
  token: string;
  isLetsbooker: boolean;
}

export interface Payload {
  email?: string;
  password?: string;
  customer?: string;
  code?: string;
  token?: string;
  isLogin?: boolean;
}

export const useLoginStore = defineStore('login', {
  state: (): State => ({
    env: process.env.ENV_NAME,
    loggedUser: null,
    resetToken: '',
  }),
  getters: {
    isLetsbooker: (state) => Boolean(state.loggedUser?.global),
    isAdminUser: (state) => state.loggedUser?.role === 'admin',
    isViewerUser: (state) => state.loggedUser?.role === 'viewer',
  },
  actions: {
    /**
     * Autenticação do email e senha para:
     * - recebimento de código (2FA) -> retorna undefined
     * - login direto sem código (2FA) -> retorna boolean se é Letsbooker
     */
    async authenticateCredentials(
      payload: Payload,
    ): Promise<boolean | undefined> {
      const { data } = await api.post<AccessTokenResponse | undefined>(
        'login',
        payload,
      );

      if (data && data.token) {
        localStorage.setItem('editor-token', data.token);
        api.defaults.headers.common.Authorization = `Bearer ${data.token}`;

        return data.isLetsbooker;
      }
    },
    /**
     * Apenas envia email com código para recuperação de senha
     */
    async sendAuthenticationCode(payload: Payload): Promise<void> {
      // validações são tratadas no componente via try/catch
      // de acordo com lançamento de erros desta requisição
      await api.post('send-auth-code', payload);
    },
    /**
     * Verificação do código de autenticação do login e recebe de informação se é um usuário letsbooker (para não ter que fazer decode)
     */
    async verifyAuthCode(payload: Payload): Promise<boolean> {
      payload.isLogin = true;

      const { data } = await api.post<AccessTokenResponse>(
        'validate-auth-code',
        payload,
      );

      localStorage.setItem('editor-token', data.token);
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;

      return data.isLetsbooker;
    },
    /**
     * Verificação do código de autenticação da redefinição de senha
     */
    async verifyResetCode(payload: Payload): Promise<string> {
      const { data } = await api.post<{ token: string }>(
        'validate-auth-code',
        payload,
      );

      return data.token;
    },
    /** Envia o token de reset e nova senha */
    async resetPassword(payload: Payload): Promise<void> {
      const { token, password } = payload;

      await api.post(
        'reset-password',
        {
          password,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    },
  },
});
